import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  NV_PATHS,
  NvAccessDialog,
  NvCertifiedListRow,
  NvEmptyState,
  NvErrorState,
  NvFilterChip,
  NvInfoIcon,
  NvLoginPrompt,
  NvSearchBar,
  NvTop10Card,
  nvAgentDetailPath,
} from "@/components/navigator";
import { useAuth } from "@/contexts/auth-context";
import { useAccessGate } from "@/hooks/use-access-gate";
import { useRequest } from "@/hooks/use-request";
import { agentDisplayName } from "@/lib/agent-display-name";
import { getCertified, getIndustryTop, getSearchSuggestions } from "@/services/navigator/home-service";

import { LockedArea, TrendingPanel, useLoginRedirect } from "../shared/agent-panels";

// F00 HOME (Figma ver.7: 847:49335 회원 · 847:49540 비회원 · 847:49639 이용 권한 안내, IA 39~50).
// 검색 → Agent Finder(해석 필터 자동 선택), Certified 7(로고 · 이름 · 공급사 · 한 줄 소개) · 주목받는 10(주간/월간) · 산업별 Top 10(범용 기본).
// △(비회원 · 승인 대기 · 공급자)는 Certified 한 줄 소개 · 주목받는 · 산업별 카드를 흐리게, 누르면 이용 권한 안내 (slide 24 · 25).
// 로그인 유도는 영역마다 한 번(10/13 제안 21): Certified 맨 위 띠 · 주목받는 / Top 10 흐린 영역 위 카드. 행마다 반복하던 문구는 자물쇠 아이콘만.

const SEARCH_PLACEHOLDER = "필요한 에이전트를 자연어로 검색해 보세요 (예: 계약서 독소조항을 자동으로 잡아주는 에이전트)";
const PENDING_NOTICE = "가입 검토가 진행 중입니다. 승인이 완료되면 모든 서비스를 이용할 수 있으며, 승인 결과는 가입하신 메일로 안내드립니다.";
const LOCKED_TEXT = "로그인 후 확인할 수 있어요";

function finderSearchPath(q: string) {
  return `${NV_PATHS.agentFinder}?${new URLSearchParams({ q, ai: "1" }).toString()}`;
}

export function NavigatorHomePage() {
  const { accessLevel, isLoading } = useAuth();
  const isGuest = accessLevel === "guest";
  const navigate = useNavigate();
  const gate = useAccessGate();
  const goLogin = useLoginRedirect();
  const [query, setQuery] = useState("");
  const [industry, setIndustry] = useState<string | undefined>(undefined);

  // Each widget loads and fails on its own: a failing one shows [다시 시도] in place, the rest of HOME stays.
  // The previous result stays on screen while the next loads (tabs are drawn from it); late responses are ignored.
  const live = { enabled: !isLoading, keepPreviousData: true };
  const suggestionsRequest = useRequest((signal) => getSearchSuggestions(signal), [accessLevel], live);
  const certifiedRequest = useRequest((signal) => getCertified(signal), [accessLevel], live);
  const topRequest = useRequest((signal) => getIndustryTop(industry, signal), [industry, accessLevel], live);
  // The recommended keywords are decoration: if they cannot load the row is simply not shown
  const suggestions = suggestionsRequest.error ? [] : (suggestionsRequest.data?.items ?? []);
  const certified = certifiedRequest.data;
  const top = topRequest.data;

  const block = () => gate.show();
  const search = (q: string) => navigate(finderSearchPath(q));
  // 왼쪽 1~5위 · 오른쪽 6~10위 (10위 미만이면 마지막 순위까지만)
  const columns = top ? [top.items.slice(0, 5), top.items.slice(5)] : [[], []];

  return (
    <div className="flex flex-col">
      {accessLevel === "pending" && (
        <div role="status" className="bg-nv-warning-subtle">
          <p className="mx-auto flex max-w-[1440px] items-center gap-[10px] px-5 py-3 text-[13px] leading-4 text-nv-warning xl:px-[120px]">
            <NvInfoIcon size={16} className="shrink-0" />
            {PENDING_NOTICE}
          </p>
        </div>
      )}

      <section className="bg-nv-bg-page">
        <div className="mx-auto flex max-w-[1440px] flex-col items-center gap-4 px-5 pb-14 pt-16 xl:px-[120px]">
          <span className="inline-flex h-[21px] items-center rounded-nv-pill bg-nv-bg-subtle px-[10px] text-[11px] font-medium leading-[13px] text-nv-text-secondary">
            엔터프라이즈급 보안과 기술력의 결합
          </span>
          <h1 className="text-center text-[34px] font-normal leading-[40.8px] text-nv-text-primary">검증된 AI 에이전트로 실현하는 비즈니스 임팩트</h1>
          <p className="text-center text-[15px] leading-[18px] text-nv-text-secondary">엔터프라이즈 환경에 맞는 PwC 검증 에이전트를 찾아보세요</p>
          <NvSearchBar value={query} onChange={setQuery} onSubmit={search} placeholder={SEARCH_PLACEHOLDER} className="min-h-[66px] w-full max-w-[880px]" />
          {suggestions.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-2">
              <span className="text-[12px] leading-[14.4px] text-nv-text-secondary">추천 검색어</span>
              {suggestions.map((term) => (
                <button
                  key={term}
                  type="button"
                  onClick={() => search(term)}
                  className="inline-flex h-6 items-center rounded-nv-pill border border-nv-border bg-nv-surface px-[10px] text-[12px] font-medium leading-[14.4px] text-nv-text-secondary hover:border-nv-border-strong hover:text-nv-text-primary"
                >
                  {term}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto grid w-full max-w-[1440px] gap-6 px-5 pb-6 pt-12 lg:grid-cols-2 xl:px-[120px]">
        <div className="flex flex-col rounded-nv-card border border-nv-border bg-nv-surface px-6 pb-4 pt-[22px]">
          <div className="flex items-start justify-between pb-2">
            <h2 className="text-[18px] font-medium leading-[22px] text-nv-text-primary">Certified Agents</h2>
            <Link to={`${NV_PATHS.agentFinder}?certified=1`} className="pt-1 text-[12px] leading-[14.4px] text-nv-text-secondary hover:text-nv-text-primary">
              더보기 ›
            </Link>
          </div>
          {!!certifiedRequest.error && <NvErrorState variant="plain" error={certifiedRequest.error} onRetry={certifiedRequest.reload} className="py-20" />}
          {!certifiedRequest.error && certified && certified.items.length === 0 && (
            <NvEmptyState variant="plain" size="sm">
              아직 PwC Certified 에이전트가 없어요.
            </NvEmptyState>
          )}
          {/* 비회원 · 승인 대기 · 공급자: 목록 맨 위에 로그인 유도 띠 한 번 (행마다 반복하던 안내 대신) */}
          {isGuest && !certifiedRequest.error && certified?.items.some((item) => item.locked) && (
            <NvLoginPrompt variant="banner" onLogin={goLogin} className="mb-2">
              로그인하면 에이전트 상세 · 도입 정보를 볼 수 있어요
            </NvLoginPrompt>
          )}
          {!certifiedRequest.error && certified?.items.map((item, i) => (
            <div key={item.id} className="relative">
              <NvCertifiedListRow
                name={item.name}
                supplier={item.supplier_name ?? undefined}
                summary={item.locked ? LOCKED_TEXT : (item.summary ?? "")}
                logoUrl={item.logo_url ?? undefined}
                toneKey={item.id}
                locked={item.locked}
                to={item.locked ? undefined : nvAgentDetailPath(item.id)}
                linkState={{ source: "home" }}
                className={i === certified.items.length - 1 ? "border-b-0" : undefined}
              />
              {item.locked && <button type="button" onClick={block} aria-label={`${item.name} 이용 권한 안내 보기`} className="absolute inset-0 z-20" />}
            </div>
          ))}
        </div>
        <TrendingPanel onBlocked={block} size="home" />
      </section>

      <section className="mx-auto flex w-full max-w-[1440px] flex-col gap-5 px-5 pb-20 pt-14 xl:px-[120px]">
        <h2 className="text-center text-[24px] font-medium leading-[28.8px] text-nv-text-primary">산업별 가장 많이 도입된 에이전트</h2>
        <div role="tablist" aria-label="산업" className="flex flex-wrap justify-center gap-2">
          {top?.tabs.map((tab) => {
            const on = tab.code === top.industry;
            return (
              <NvFilterChip
                key={tab.code}
                role="tab"
                aria-selected={on}
                selected={on}
                onClick={() => setIndustry(tab.code)}
              >
                {tab.label}
              </NvFilterChip>
            );
          })}
        </div>
        {top?.label && <h3 className="text-[15px] font-medium leading-[18px] text-nv-text-primary">{top.label} · Top 10</h3>}
        {!!topRequest.error && <NvErrorState variant="plain" error={topRequest.error} onRetry={topRequest.reload} className="py-16" />}
        {!topRequest.error && top && top.items.length === 0 && (
          <NvEmptyState variant="plain" size="sm" className="py-16">
            이 산업의 에이전트가 아직 없어요.
          </NvEmptyState>
        )}
        {!topRequest.error && top && top.items.length > 0 && (
          <LockedArea locked={top.locked} onBlocked={block} prompt={isGuest ? { text: "로그인하면 산업별 Top 10 을 볼 수 있어요", onLogin: goLogin } : undefined}>
            <div className="grid gap-5 md:grid-cols-2">
              {columns.map((column, c) => (
                <div key={c} className="flex flex-col gap-3">
                  {column.map((item) => (
                    <NvTop10Card
                      key={item.id}
                      rank={item.rank}
                      name={agentDisplayName(item.name)}
                      summary={item.locked ? LOCKED_TEXT : (item.summary ?? "")}
                      reviewCount={item.locked || !item.view_count ? undefined : item.view_count}
                      to={item.locked ? undefined : nvAgentDetailPath(item.id)}
                      linkState={{ source: "home" }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </LockedArea>
        )}
      </section>

      <NvAccessDialog {...gate.dialogProps} />
    </div>
  );
}
